<template>
  <div class="task-container ">
    <!-- 查询条件 -->
    <div class="search-wrapper">
      <div class="search-title">{{ $t('task.search.title') }}</div>
      <div class="search-content">
        <el-input v-model="searchForm.taskName" :placeholder="$t('task.search.taskName')" class="search-item" clearable
          @keyup.enter.native="handleSearch" />

        <el-button type="primary" @click="handleSearch">{{ $t('common.search') }}</el-button>
      </div>
    </div>

    <div class="list-wrapper">
      <div class="list-header">
        <div class="list-title">{{ $t('task.list.title') }}</div>
        <el-button type="primary" @click="handleAdd">{{ $t('common.add') }}</el-button>
      </div>

      <el-table v-loading="loading" :data="tableData" border style="width: 100%">
        <el-table-column prop="taskId" :label="$t('task.table.taskId')" />
        <el-table-column prop="taskName" :label="$t('task.table.taskName')" />
        <el-table-column prop="templateName" :label="$t('task.table.templateId')" />
        <el-table-column prop="taskCron" :label="$t('task.table.taskCron')" />
        <el-table-column :label="$t('task.table.state')">
          <template slot-scope="scope">
            <span v-if="scope.row.state == '001'">STOP</span>
            <span v-else>RUN</span>
          </template>
        </el-table-column>
        <el-table-column prop="createTime" :label="$t('task.table.createTime')" />
        <el-table-column :label="$t('common.operation')" width="200" fixed="right">
          <template slot-scope="scope">
            <el-button size="mini" type="primary" @click="handleEdit(scope.row)">{{ $t('common.edit') }}</el-button>
            <el-button size="mini" type="danger" @click="handleDelete(scope.row)">{{ $t('common.delete') }}</el-button>
            <el-button size="mini" type="warn" @click="handleStart(scope.row)" v-if="scope.row.state == '001'">{{
              $t('task.start') }}</el-button>
            <el-button size="mini" type="warn" @click="handleStop(scope.row)" v-else>{{ $t('task.stop') }}</el-button>
            <el-button size="mini" type="warn" @click="handleTest(scope.row)" v-if="scope.row.state == '002'"
              :loading="testingTaskId === scope.row.taskId" :disabled="!!testingTaskId">Test</el-button>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination :current-page.sync="page.current" :page-sizes="[10, 20, 30, 50]" :page-size="page.size"
        :total="page.total" layout="total, sizes, prev, pager, next, jumper" @size-change="handleSizeChange"
        @current-change="handleCurrentChange" />
    </div>
    <AddTask :visible.sync="addVisible" @success="handleSuccess" />

    <EditTask :visible.sync="editVisible" :form-data="currentRow" @success="handleSuccess" />

    <DelTask :visible.sync="delVisible" :task-id="currentRow.taskId" @success="handleSuccess" />

    <el-dialog :title="$t('task.result.title')" :visible.sync="resultVisible" width="700px" append-to-body>
      <div v-if="taskResult.data" class="result-summary">
        <el-tag :type="resultTagType" size="small">{{ resultStateText }}</el-tag>
        <span class="result-msg">{{ taskResult.data.msg }}</span>
        <el-progress v-if="isTaskRunning" :percentage="resultPercentage" class="result-progress" />
      </div>
      <el-descriptions v-if="taskResult.data" :column="3" border size="mini" class="result-detail">
        <el-descriptions-item :label="$t('task.result.communityCount')">{{ taskResult.data.communityCount }}</el-descriptions-item>
        <el-descriptions-item :label="$t('task.result.dealtCount')">{{ taskResult.data.dealtCount || 0 }}</el-descriptions-item>
        <el-descriptions-item :label="$t('task.result.successCount')">{{ taskResult.data.successCount }}</el-descriptions-item>
        <el-descriptions-item :label="$t('task.result.failCount')">
          <span :class="{ 'result-fail': taskResult.data.failCount > 0 }">{{ taskResult.data.failCount }}</span>
        </el-descriptions-item>
        <el-descriptions-item :label="$t('task.result.retryCount')">{{ taskResult.data.retryCount }}</el-descriptions-item>
        <el-descriptions-item :label="$t('task.result.errorCount')">{{ taskResult.data.errorCount || 0 }}</el-descriptions-item>
        <el-descriptions-item :label="$t('task.result.repairedCount')">{{ taskResult.data.repairedCount || 0 }}</el-descriptions-item>
        <el-descriptions-item :label="$t('task.result.elapsed')">{{ taskResult.data.elapsedSeconds }}</el-descriptions-item>
        <el-descriptions-item :label="$t('task.result.currentCommunity')">{{ taskResult.data.currentCommunityName || '-' }}</el-descriptions-item>
      </el-descriptions>
      <el-table v-if="taskResult.data && taskResult.data.failCommunities && taskResult.data.failCommunities.length"
        :data="taskResult.data.failCommunities" size="mini" border class="result-fail-table" :row-class-name="failRowClass">
        <el-table-column prop="communityName" :label="$t('task.result.communityName')" width="180" />
        <el-table-column prop="communityId" :label="$t('task.result.communityId')" width="150" />
        <el-table-column prop="elapsedMs" :label="$t('task.result.elapsedMs')" width="100" />
        <el-table-column prop="errMsg" :label="$t('task.result.errMsg')" />
      </el-table>
      <span slot="footer">
        <el-button v-if="!isTaskRunning && taskResult.data && taskResult.data.failCount > 0" type="primary"
          :loading="retrying" @click="handleRetryFail">{{ $t('task.result.retryFail') }}</el-button>
        <el-button @click="resultVisible = false">{{ $t('task.result.close') }}</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { getTaskList, startTask, stopTask, runTask, queryRunResult } from '@/api/dev/taskApi'
import AddTask from '@/components/dev/AddTask'
import EditTask from '@/components/dev/EditTask'
import DelTask from '@/components/dev/DelTask'

export default {
  name: 'taskList',
  components: {
    AddTask,
    EditTask,
    DelTask
  },
  data() {
    return {
      loading: false,
      searchForm: {
        taskName: '',
        templateId: '',
        taskCron: '',
        state: '',
      },
      tableData: [],
      page: {
        current: 1,
        size: 10,
        total: 0
      },
      addVisible: false,
      editVisible: false,
      delVisible: false,
      currentRow: {},
      testingTaskId: '',
      retrying: false,
      resultVisible: false,
      taskResult: {},
      pollTimer: null,
      pollStartTime: 0,
      pollMaxTime: 1800000
    }
  },
  computed: {
    isTaskRunning() {
      const state = this.taskResult.data ? this.taskResult.data.state : ''
      return state === 'RUNNING' || state === 'SUBMITTED'
    },
    resultStateText() {
      const state = this.taskResult.data ? this.taskResult.data.state : ''
      if (state === 'RUNNING' || state === 'SUBMITTED') {
        return this.$t('task.result.running')
      }
      if (state === 'DONE') {
        return this.$t('task.result.done')
      }
      if (state === 'REJECTED') {
        return this.$t('task.result.rejected')
      }
      if (state === 'NONE') {
        return this.$t('task.result.none')
      }
      return this.$t('task.result.fail')
    },
    resultTagType() {
      if (this.isTaskRunning) {
        return 'info'
      }
      const state = this.taskResult.data ? this.taskResult.data.state : ''
      return state === 'DONE' ? 'success' : 'danger'
    },
    resultPercentage() {
      if (!this.taskResult.data || !this.taskResult.data.communityCount) {
        return 0
      }
      const dealt = this.taskResult.data.dealtCount || 0
      return Math.min(100, Math.floor(dealt * 100 / this.taskResult.data.communityCount))
    }
  },
  created() {
    this.getList()
  },
  beforeDestroy() {
    this.stopPolling()
  },
  methods: {
    async getList() {
      try {
        this.loading = true
        const params = {
          page: this.page.current,
          row: this.page.size,
          taskName: this.searchForm.taskName,
          templateId: this.searchForm.templateId,
          taskCron: this.searchForm.taskCron,
          state: this.searchForm.state,
          createTime: this.searchForm.createTime,
        }
        const { data, total } = await getTaskList(params)
        this.tableData = data
        this.page.total = total
      } catch (error) {
        this.$message.error(this.$t('task.fetchError'))
      } finally {
        this.loading = false
      }
    },
    async handleStart(row) {
      try {
        this.loading = true
        await startTask(row.taskId)
        this.getList()
      } catch (error) {
        this.$message.error(error)
      } finally {
        this.loading = false
      }
    },
    async handleStop(row) {
      try {
        this.loading = true
        await stopTask(row.taskId)
        this.getList()
      } catch (error) {
        this.$message.error(error)
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.page.current = 1
      this.getList()
    },
    handleAdd() {
      this.addVisible = true
    },
    handleEdit(row) {
      this.currentRow = { ...row }
      this.editVisible = true
    },
    handleDelete(row) {
      this.currentRow = { ...row }
      this.delVisible = true
    },
    async handleTest(row) {
      if (this.testingTaskId) {
        return
      }
      this.testingTaskId = row.taskId
      this.currentRow = { ...row }
      this.resultVisible = false
      try {
        const res = await runTask(row.taskId)
        this.taskResult = res || {}
        this.resultVisible = true
        if (this.isTaskRunning) {
          this.$message.success(this.$t('task.submitted'))
          this.startPolling(row.taskId)
        } else {
          this.testingTaskId = ''
        }
      } catch (error) {
        this.testingTaskId = ''
        const msg = error && error.message ? error.message : ''
        if (msg.indexOf('timeout') > -1 || msg.indexOf('Network') > -1) {
          this.$message.warning(this.$t('task.timeout'))
        } else {
          this.$message.error(msg || this.$t('task.testError'))
        }
      }
    },
    startPolling(taskId) {
      this.stopPolling()
      this.pollStartTime = Date.now()
      this.pollTimer = setInterval(() => {
        this.queryResult(taskId)
      }, 2000)
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },
    async queryResult(taskId) {
      if (Date.now() - this.pollStartTime > this.pollMaxTime) {
        this.stopPolling()
        this.testingTaskId = ''
        this.$message.warning(this.$t('task.pollTimeout'))
        return
      }
      try {
        const res = await queryRunResult(taskId)
        this.taskResult = res || {}
        if (!this.isTaskRunning) {
          this.stopPolling()
          this.testingTaskId = ''
        }
      } catch (error) {
        this.stopPolling()
        this.testingTaskId = ''
        const msg = error && error.message ? error.message : ''
        this.$message.error(msg || this.$t('task.testError'))
      }
    },
    async handleRetryFail() {
      if (this.retrying || !this.taskResult.data || !this.taskResult.data.failCommunities) {
        return
      }
      const communityIds = this.taskResult.data.failCommunities.map(item => item.communityId).join(',')
      if (!communityIds) {
        return
      }
      this.retrying = true
      try {
        const res = await runTask(this.currentRow.taskId, communityIds)
        this.taskResult = res || {}
        this.getList()
        if (this.isTaskRunning) {
          this.startPolling(this.currentRow.taskId)
        }
      } catch (error) {
        const msg = error && error.message ? error.message : ''
        this.$message.error(msg || this.$t('task.testError'))
      } finally {
        this.retrying = false
      }
    },
    failRowClass() {
      return 'result-fail-row'
    },
    handleSuccess() {
      this.getList()
    },
    handleSizeChange(val) {
      this.page.size = val
      this.getList()
    },
    handleCurrentChange(val) {
      this.page.current = val
      this.getList()
    }
  }
}
</script>

<style lang="scss" scoped>
.task-container {
  padding: 20px;
  margin: 0;

  .search-wrapper {
    background-color: #fff;
    padding: 20px;
    margin-bottom: 20px;
    border-radius: 4px;

    .search-title {
      font-size: 16px;
      font-weight: bold;
      margin-bottom: 20px;
      text-align: left;
      color: #333;
    }

    .search-content {
      display: flex;
      align-items: center;
      gap: 20px;

      .search-item {
        width: 200px;
      }
    }
  }

  .list-wrapper {
    background-color: #fff;
    padding: 20px;
    border-radius: 4px;

    .list-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 20px;

      .list-title {
        font-size: 16px;
        font-weight: bold;
        color: #333;
      }
    }

    .el-pagination {
      margin-top: 20px;
      text-align: right;
    }
  }
}
</style>

<style lang="scss">
.result-summary {
  margin-bottom: 12px;

  .result-msg {
    margin-left: 10px;
    color: #606266;
  }

  .result-progress {
    margin-top: 10px;
  }
}

.result-detail {
  margin-bottom: 12px;
}

.result-fail {
  color: #f56c6c;
  font-weight: bold;
}

.result-fail-table {
  margin-top: 10px;
}

.result-fail-row {
  background-color: #fef0f0;
}
</style>